import { getPreviewThumbnailSignedUrl } from '@/api';
import buttonBack from '@/assets/btn/back.png';
import buttonLanguage from '@/assets/btn/language.png';
import buttonNext from '@/assets/btn/next.png';
import buttonPause from '@/assets/btn/pause.svg';
import buttonPlay from '@/assets/btn/play.svg';
import buttonPrev from '@/assets/btn/prev.png';
import checked from '@/assets/icons/checked.svg';
import barIcon from '@/assets/icons/icon-babyshark.svg';
import { ClientAuth, Device, Sound } from '@/classes';
import { SECOND_MS } from '@/constants';
import { yellowFocusedBorder } from '@/constants/cssBundle';
import { keyCodes } from '@/constants/keyCode';
import { PopupContext } from '@/contexts/PopupContext';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { useFocusElement } from '@/hooks/useFocusElement';
import { Player } from '@/hooks/usePlayer';
import { useRouter } from '@/hooks/useRouter';
import { FocusableButton, FocusableImage } from '@/shared/ui';
import { Video } from '@/types';
import { getResolution } from '@/utils';
import { formatSecondsToHMS } from '@/utils/format';
import { FocusContext, getCurrentFocusKey, setFocus } from '@noriginmedia/norigin-spatial-navigation';
import debounce from 'lodash/debounce';
import { parse, stringify } from 'qs';
import React, {
  MouseEventHandler,
  TouchEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const PLAYER_BUTTON_CSS = `w-[78px] h-[78px] p-3 bg-[#FFF9D7] rounded-full flex justify-center items-center ${yellowFocusedBorder} transition-all duration-300 outline-disable col-margin-gap-26px`;

type FocusableProgressBarProps = {
  id?: number;
  duration: number;
  currentTime: number;
  seekTime: number;
  focusKey: string;
  isPlaying: boolean;
  player?: Player | undefined;
  language?: string;
  previewSize?: number;
  previewThumbnailCount?: number | null;
  handleDrag: MouseEventHandler<HTMLDivElement>;
  handleTouch: TouchEventHandler<HTMLDivElement>;
};

const cache: Record<string, string> = {};

const FocusableProgressBar = ({
  id,
  duration,
  currentTime,
  seekTime,
  focusKey,
  isPlaying,
  player,
  language,
  previewSize = 320,
  previewThumbnailCount,
  handleDrag,
  handleTouch,
}: FocusableProgressBarProps) => {
  const { ref, focused, focusSelf } = useExtendFocusable({ focusKey: focusKey });
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [bufferedRanges, setBufferedRanges] = useState<Array<[number, number]>>([]);
  const loadSignedUrl = useMemo(
    () =>
      debounce(async (id: number, language: string, index: number) => {
        try {
          const cacheKey = `${id}-${language}-${index}`;
          const signedUrl = await getPreviewThumbnailSignedUrl({ id, language, index });
          const response = await fetch(signedUrl);
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          cache[cacheKey] = blobUrl;
          setSignedUrl(blobUrl);

          const resolution = await getResolution(blob);
          setCrop((prev) => ({
            x: prev?.x || 0,
            y: prev?.y || 0,
            width: Math.floor(resolution.width / 6),
            height: Math.floor(resolution.height / 6),
          }));
          setLoading(false);
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            console.error('썸네일 쿼리 실패', e);
          }
        }
      }, 150),
    [],
  );

  const parseBlobURL = useCallback(async (index: number) => {
    setCrop((prev) => ({
      width: prev?.width || 1,
      height: prev?.height || 1,
      x: (index % 6) * (prev?.width || 1),
      y: (Math.floor(index / 6) % 6) * (prev?.height || 1),
    }));
  }, []);

  const seeking = useMemo(() => {
    const currentFocusKey = getCurrentFocusKey();
    return currentFocusKey === 'progressBar' && player && player.isPaused && Math.abs(seekTime - currentTime) >= 2;
  }, [player, seekTime, currentTime]);

  const wrapperStyle = useMemo<React.CSSProperties>(
    () =>
      seeking && loading && !player?.isSeekLoading
        ? {
            left: `${216 + (seekTime / duration) * 1610}px`,
            bottom: '220px',
            transform: `translateX(-50%) scale(${previewSize / 160})`,
            width: 160,
            height: 90,
          }
        : signedUrl && crop && seeking && !player?.isSeekLoading
          ? {
              left: `${216 + (seekTime / duration) * 1610}px`,
              bottom: '220px',
              transform: `translateX(-50%) scale(${previewSize / crop.width})`,
              width: crop.width,
              height: crop.height,
              background: `url(${signedUrl}) -${crop.x}px -${crop.y}px, black`,
              imageRendering: 'pixelated',
            }
          : {
              visibility: 'hidden',
            },
    [seeking, loading, player?.isSeekLoading, seekTime, duration, previewSize, signedUrl, crop],
  );

  const onClickPlay = useCallback(() => {
    if (player) {
      if (isPlaying) {
        player.pause();
      } else {
        if (seekTime !== player.currentTime) {
          player.seek(seekTime);
          player.play();
        } else {
          player.play();
        }
      }
    }
    focusSelf();
  }, [player, focusSelf, isPlaying, seekTime]);

  useFocusElement({ ref, focused, onClick: () => {}, keyCode: keyCodes.ENTER });

  useEffect(() => {
    if (id && language && previewThumbnailCount) {
      if (seeking) {
        const COUNT_PER_SHEET = 36;
        const index = Math.max(
          0,
          Math.min(previewThumbnailCount - 1, Math.round((seekTime / duration) * (previewThumbnailCount - 1)) + 1),
        );
        const sheetIndex = Math.floor(index / COUNT_PER_SHEET);
        const cacheKey = `${id}-${language}-${sheetIndex}`;

        if (cache[cacheKey]) {
          setLoading(false);
          setSignedUrl(cache[cacheKey]);
        } else {
          setLoading(true);
          loadSignedUrl(id, language, sheetIndex);
        }
      }
    }
  }, [id, language, previewThumbnailCount, seekTime, duration, loadSignedUrl, seeking]);

  useEffect(() => {
    if (signedUrl && previewThumbnailCount) {
      const index = Math.max(
        0,
        Math.min(previewThumbnailCount - 1, Math.round((seekTime / duration) * (previewThumbnailCount - 1)) + 1),
      );
      parseBlobURL(index);
    }
  }, [duration, parseBlobURL, previewThumbnailCount, seekTime, signedUrl]);

  useEffect(() => {
    const current = player?.ref.current;
    if (current) {
      const updateBufferedRanges = () => {
        const ranges: Array<[number, number]> = [];
        for (let i = 0; i < current.buffered.length; i++) {
          ranges.push([current.buffered.start(i), current.buffered.end(i)]);
        }
        setBufferedRanges(ranges);
      };
      const interval = setInterval(updateBufferedRanges, 1000);
      updateBufferedRanges();

      return () => {
        clearInterval(interval);
      };
    }
  }, [player?.ref]);

  useEffect(() => {
    return () => {
      for (const key in cache) {
        if (cache[key] && typeof cache[key] === 'string' && cache[key].startsWith('blob:')) {
          URL.revokeObjectURL(cache[key]);
        }
        delete cache[key];
      }
    };
  }, []);

  return (
    <>
      <button
        className="absolute rounded-[20px] left-[88px] bottom-[84px] w-[86px] h-[78px] bg-transparent flex justify-center items-center transition-all duration-300 hover-bg-secondary"
        onClick={onClickPlay}
        onContextMenu={onClickPlay}
        style={focused ? { backgroundColor: '#FFD100' } : {}}
      >
        {isPlaying ? (
          <img className="w-[70px] h-[70px]" src={buttonPause} alt="Pause" />
        ) : (
          <img className="w-[70px] h-[70px]" src={buttonPlay} alt="Play" />
        )}
      </button>

      <div
        ref={ref}
        tabIndex={0}
        className="fixed left-[216px] bottom-[111px] w-[1616px] h-[20px] rounded-full flex items-center outline-disable overflow-hidden"
        onMouseDown={handleDrag}
        onTouchStart={handleTouch}
      >
        <div className="absolute left-0 h-full bg-gray-200 rounded-full w-full" />
        {bufferedRanges.map(([start, end]) => (
          <div
            key={`${start}-${end}`}
            className="absolute left-0 h-full bg-white"
            style={{
              left: `${(start / duration) * 100}%`,
              width: `${(((end - start) / duration) * 100).toFixed(2)}%`,
            }}
          />
        ))}
        <div
          className="absolute left-0 h-full bg-[#FFD100] opacity-50"
          style={{ width: `${((seekTime / duration) * 100).toFixed(2)}%` }}
        />
        <div
          className="absolute left-0 h-full bg-[#FFD100] opacity-50"
          style={{ width: `${((currentTime / duration) * 100).toFixed(2)}%` }}
        />
      </div>
      <img
        src={barIcon}
        className="absolute ease-linear bottom-[88px] w-[66px] h-[66px]"
        style={{
          opacity: focused ? '1' : '0',
          left: `${188 + (seekTime / duration) * 1610}px`,
          pointerEvents: 'none',
        }}
      />

      <div
        className="absolute rounded-[6px] overflow-hidden bg-black flex justify-center items-center z-[1]"
        style={wrapperStyle}
      >
        {loading && (
          <div className="w-[40px] h-[40px] border-4 border-yellow-300 border-t-transparent rounded-full animate-spin" />
        )}
      </div>
    </>
  );
};

export type PlayerControllerProps = {
  video?: Video;
  player: Player;
  sourcePage?: string;
  containerId?: number;
};

export const PlayerController = ({ video, player, sourcePage, containerId }: PlayerControllerProps) => {
  const router = useRouter();
  const popupContext = useContext(PopupContext);
  const [controllerState, setControllerState] = useState<'show' | 'hide' | 'showLanguageModal'>('show');
  const dimRef = useRef<HTMLDivElement>(null);
  const { ref: navigationRef, focusKey } = useExtendFocusable({
    focusable: true,
    saveLastFocusedChild: false,
    trackChildren: true,
    autoRestoreFocus: true,
    isFocusBoundary: true,
  });

  const [localCurrentTime, setLocalCurrentTime] = useState(player.currentTime || 0);
  const [interactiveAt, setInteractiveAt] = useState(Date.now());

  const handleStopButton = useCallback(() => {
    player.pause();
    player.seek(0);
    setLocalCurrentTime(0);
    router.pop();
  }, [player, router]);

  const handleDragStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (player.isPlaying) {
        player.pause();
      }
      const ratio = window.innerWidth / 1920;
      const rangeX = [Math.round(216 * ratio), Math.round(1832 * ratio)];
      let progress = Math.max(0, Math.min(1, (e.clientX - rangeX[0]) / (rangeX[1] - rangeX[0])));

      setInteractiveAt(Date.now());

      const handleMouseMove = (e: MouseEvent) => {
        setInteractiveAt(Date.now());
        const currentX = e.clientX;
        progress = Math.max(0, Math.min(1, (currentX - rangeX[0]) / (rangeX[1] - rangeX[0])));

        setLocalCurrentTime(Math.max(0, Math.min(Math.floor(progress * player.duration), player.duration)));
      };

      setLocalCurrentTime(Math.max(0, Math.min(Math.floor(progress * player.duration), player.duration - 1)));
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener(
        'mouseup',
        (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          window.removeEventListener('mousemove', handleMouseMove);
          player.seek(progress * player.duration);
          player.play();
        },
        { once: true },
      );
    },
    [player],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.targetTouches.length === 1) {
        if (player.isPlaying) {
          player.pause();
        }

        const event = e.targetTouches[0];
        const ratio = window.innerWidth / 1920;
        const rangeX = [Math.round(216 * ratio), Math.round(1832 * ratio)];
        let progress = Math.max(0, Math.min(1, (event.clientX - rangeX[0]) / (rangeX[1] - rangeX[0])));

        setInteractiveAt(Date.now());

        const handleTouchMove = (e: TouchEvent) => {
          setInteractiveAt(Date.now());
          if (e.targetTouches.length === 1) {
            const event = e.targetTouches[0];
            const currentX = event.clientX;
            progress = Math.max(0, Math.min(1, (currentX - rangeX[0]) / (rangeX[1] - rangeX[0])));

            setLocalCurrentTime(Math.max(0, Math.min(Math.floor(progress * player.duration), player.duration)));
          }
        };

        setLocalCurrentTime(Math.max(0, Math.min(Math.floor(progress * player.duration), player.duration)));
        window.addEventListener('touchmove', handleTouchMove);
        window.addEventListener(
          'touchend',
          (e: TouchEvent) => {
            e.preventDefault();
            e.stopPropagation();
            window.removeEventListener('touchmove', handleTouchMove);
            player.seek(progress * player.duration);
            player.play();
          },
          { once: true },
        );
      }
    },
    [player],
  );

  const onPrev = useCallback(
    (duration?: number) => {
      if (video && video.prevContent && video.prevContent.type === 'VIDEO') {
        if (!ClientAuth.user?.isSubscriber && video.prevContent.accessScope === 'SUBSCRIBER') {
          if (popupContext) {
            const currentFocusKey = getCurrentFocusKey();
            popupContext.showSubscribePopup('player', currentFocusKey);
            player.pause();
          }
        } else {
          router.push(
            `/videos?${stringify({
              id: video.prevContent.id,
              cursor: video.prevContent.cursor,
              duration, // ! duration 이 존재하면 이어보기 PopUp이 안나오고 해당 Duration부터 바로 재생된다.
              source_page: sourcePage,
              container_id: containerId,
            })}`,
            { replace: true },
          );
        }
      }
    },
    [containerId, player, popupContext, router, sourcePage, video],
  );

  const onNext = useCallback(
    (duration?: number) => {
      if (video && video.nextContent && video.nextContent.type === 'VIDEO') {
        if (!ClientAuth.user?.isSubscriber && video.nextContent.accessScope === 'SUBSCRIBER') {
          if (popupContext) {
            const currentFocusKey = getCurrentFocusKey();
            popupContext.showSubscribePopup('player', currentFocusKey);
            player.pause();
          }
        } else {
          router.push(
            `/videos?${stringify({
              id: video.nextContent.id,
              cursor: video.nextContent.cursor,
              duration, // ! duration 이 존재하면 이어보기 PopUp이 안나오고 해당 Duration부터 바로 재생된다.
              source_page: sourcePage,
              container_id: containerId,
            })}`,
            { replace: true },
          );
        }
      }
    },
    [containerId, player, popupContext, router, sourcePage, video],
  );

  const handleKeyboardEvent = useCallback(
    (e: KeyboardEvent) => {
      const currentFocusKey = getCurrentFocusKey();

      const isPressedCloseButton =
        e.keyCode === 10009 ||
        e.keyCode === 461 ||
        e.code === 'Backspace' ||
        e.code === 'Escape' ||
        e.key === 'Backspace' ||
        e.key === 'Escape';

      if (isPressedCloseButton) {
        e.preventDefault();
        e.stopPropagation();
        handleStopButton();
        return;
      }

      setInteractiveAt(Date.now());
      if (controllerState === 'hide') {
        switch (e.keyCode) {
          case 119:
          case keyCodes.ENTER: {
            if (player.isPlaying) {
              player.pause();
            } else {
              if (localCurrentTime !== player.currentTime) {
                player.seek(localCurrentTime);
                player.play();
              } else {
                player.play();
              }
            }
            break;
          }
          case keyCodes.Stop: {
            handleStopButton();
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          case keyCodes.Pause: {
            if (player.isPlaying) {
              player.pause();
            }
            break;
          }
          case keyCodes.Play: {
            if (!player.isPlaying) {
              if (localCurrentTime !== player.currentTime) {
                player.seek(localCurrentTime);
                player.play();
              } else {
                player.play();
              }
            }
            break;
          }
        }
        setControllerState('show');
        setFocus('progressBar');
        e.stopPropagation();
        return;
      }

      // https://developer.samsung.com/smarttv/develop/guides/user-interaction/remote-control.html
      switch (e.keyCode) {
        case keyCodes.ENTER: {
          document.activeElement?.dispatchEvent(new KeyboardEvent('keydown', { code: 'Space' }));

          if (currentFocusKey !== 'progressBar') return;
          Sound.press();
          if (player.isPlaying) {
            player.pause();
            return;
          } else {
            if (localCurrentTime !== player.currentTime) {
              player.seek(localCurrentTime);
              player.play();
            } else {
              player.play();
            }
          }
          break;
        }
        case keyCodes.DOWN: {
          if (currentFocusKey !== 'progressBar') return;
          e.preventDefault();
          e.stopPropagation();
          setControllerState('hide');
          break;
        }
        case keyCodes.UP: {
          if (currentFocusKey !== 'progressBar') return;
          e.preventDefault();
          e.stopPropagation();
          if (video) {
            if (video.prevContent) {
              setFocus('prevButton');
            } else if (video.nextContent) {
              setFocus('nextButton');
            } else {
              setFocus('languageButton');
            }
          } else {
            setFocus('backButton');
          }
          break;
        }
        case keyCodes.LEFT: {
          if (currentFocusKey !== 'progressBar' || !player.isLoaded) return;
          const time = Math.max(0, Math.min(localCurrentTime - 5, player.duration));

          if (player.isPlaying) {
            player.pause();
          } else {
            setLocalCurrentTime(time);
          }
          e.preventDefault();
          e.stopPropagation();
          break;
        }
        case keyCodes.RIGHT: {
          if (currentFocusKey !== 'progressBar' || !player.isLoaded) return;
          const time = Math.max(0, Math.min(localCurrentTime + 5, player.duration));

          if (player.isPlaying) {
            player.pause();
          } else {
            setLocalCurrentTime(time);
          }
          e.preventDefault();
          e.stopPropagation();
          break;
        }
        case keyCodes.Rewind: {
          const time = Math.max(0, Math.min(localCurrentTime - 5, player.duration));

          if (player.isPlaying) {
            player.pause();
          } else {
            setLocalCurrentTime(time);
          }
          e.preventDefault();
          e.stopPropagation();
          break;
        }
        case keyCodes.FastForward: {
          const time = Math.max(0, Math.min(localCurrentTime + 5, player.duration));

          if (player.isPlaying) {
            player.pause();
          } else {
            setLocalCurrentTime(time);
          }
          e.preventDefault();
          e.stopPropagation();
          break;
        }
        case keyCodes.Stop: {
          Sound.press();
          handleStopButton();
          e.preventDefault();
          e.stopPropagation();
          break;
        }
        case keyCodes.Pause: {
          Sound.press();
          player.pause();
          break;
        }
        case keyCodes.Play: {
          if (!player.isPlaying) {
            Sound.press();
            if (localCurrentTime !== player.currentTime) {
              player.seek(localCurrentTime);
              player.play();
            } else {
              player.play();
            }
          }
          break;
        }
        case 10232: {
          if (video && video.prevContent) {
            Sound.press();
            onPrev();
          }
          break;
        }

        case 10233: {
          if (video && video.nextContent) {
            Sound.press();
            onNext();
          }
        }
      }
    },
    [controllerState, handleStopButton, localCurrentTime, onNext, onPrev, player, video],
  );

  const onTouchStart = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      if (controllerState === 'hide') {
        setInteractiveAt(Date.now());
        setControllerState('show');
        setFocus('progressBar');
      } else {
        setControllerState('hide');
      }
    },
    [controllerState],
  );

  const handleMouseEvent = useCallback(() => {
    setInteractiveAt(Date.now());

    if (controllerState === 'hide') {
      setControllerState('show');
      setFocus('progressBar');
    }
  }, [controllerState]);

  const onChangeLanguage = useCallback(
    (language: string) => {
      if (video && video.languages.includes(language)) {
        if (video.currentLanguage !== language) {
          const pathname = location.pathname.replace(process.env.NODE_ENV === 'development' ? '/smart-tv' : '', '');
          const query = parse(location.search, { ignoreQueryPrefix: true });

          if (player.currentTime) {
            query.duration = player.currentTime.toString();
          }

          Device.videoLanguage = language;

          router.push(`${pathname}?${stringify({ ...query, language })}`, { replace: true });
        } else {
          setControllerState('hide');
        }
      }
    },
    [player.currentTime, router, video],
  );

  const languageSelectComponents = useMemo(() => {
    const languages = [
      { code: 'ko', name: '한국어' },
      { code: 'en', name: 'English' },
      { code: 'zh', name: '中文' },
      { code: 'pt', name: 'Português' },
      { code: 'es', name: 'Español' },
      { code: 'ja', name: '日本語' },
      { code: 'id', name: 'Bahasa Indonesia' },
    ];
    const components = [];

    for (const language of languages) {
      if (video && video.languages.includes(language.code)) {
        const focusKey = `lang_${language.code}`;
        const onClick = () => {
          onChangeLanguage(language.code);
        };

        components.push(
          <FocusableButton
            key={focusKey}
            focusKey={focusKey}
            className="relative text-[34px] text-bold leading-[39.84px] text-left w-full h-[80px] px-[20px] py-[16px] rounded-[10px] outline-disable outline-secondary-8 row-margin-gap-22px bg-transparent"
            onClick={onClick}
          >
            {language.name}
            {video.currentLanguage === language.code && (
              <img src={checked} className="absolute top-[20px] right-[20px]" />
            )}
          </FocusableButton>,
        );
      }
    }

    return components;
  }, [onChangeLanguage, video]);

  // ! Keyboard Event
  useEffect(() => {
    if (document) {
      document.addEventListener('keydown', handleKeyboardEvent);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyboardEvent);
    };
  }, [handleKeyboardEvent]);

  // ! Touch Event
  useEffect(() => {
    const current = dimRef.current;
    if (current) {
      current.addEventListener('touchstart', onTouchStart);

      return () => {
        current.removeEventListener('touchstart', onTouchStart);
      };
    }
  }, [onTouchStart]);

  // ! Mouse Event
  useEffect(() => {
    if (document) {
      document.addEventListener('mousemove', handleMouseEvent);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseEvent);
    };
  }, [handleMouseEvent]);

  // ! Controller State Update
  useEffect(() => {
    if (player.isPlaying) {
      // ! Controller Hide...
      if (controllerState === 'show' && interactiveAt > 0 && Date.now() - interactiveAt > 5 * SECOND_MS) {
        setControllerState('hide');
        setLocalCurrentTime(player.currentTime);
      }

      // ! Current Time Update...
      const currentFocusKey = getCurrentFocusKey();
      setLocalCurrentTime((prev) => {
        const diff = Math.abs(prev - player.currentTime);
        if (currentFocusKey === 'progressBar' && diff >= 2) {
          return prev;
        } else {
          return player.currentTime;
        }
      });
    } else {
      // ! Controller Hide...
      const interval = setInterval(() => {
        if (controllerState === 'show' && interactiveAt > 0 && Date.now() - interactiveAt > 5 * SECOND_MS) {
          setControllerState('hide');
          if (!player.isSeekLoading) {
            setLocalCurrentTime(player.currentTime);
          }
        }
      }, SECOND_MS);

      return () => {
        clearInterval(interval);
      };
    }
  }, [controllerState, interactiveAt, player.currentTime, player.isPlaying, player.isSeekLoading]);

  useEffect(() => {
    if (controllerState === 'showLanguageModal' && video && video.currentLanguage) {
      setFocus(`lang_${video.currentLanguage}`);
    }
  }, [controllerState, video]);

  useEffect(() => {
    setFocus('progressBar');
  }, []);

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={navigationRef} className="fixed bottom-0 left-0 w-screen h-screen">
        <div className="w-full h-full" ref={dimRef} />
        {controllerState === 'show' && (
          <div className="absolute top-0 left-0 w-full h-[200px] flex justify-center items-center">
            <div className="absolute top-0 left-0 right-0 bottom-0 transition duration-300 bg-gradient-to-t from-transparent via-[rgba(0,0,0,0.5)] via-100% to-[rgba(0,0,0,0.5)]" />
            <FocusableImage
              src={buttonBack}
              focusKey="backButton"
              className="absolute top-[40px] left-[40px] focus:bg-[#FFD100] hover-bg-secondary p-[20px] w-[90px] h-[90px] rounded-[20px] outline-disable"
              onClick={handleStopButton}
            />
          </div>
        )}
        {controllerState === 'show' && (
          <div className="absolute bottom-0 left-0 w-full h-[200px] flex justify-center items-center">
            <div className="absolute top-0 left-0 right-0 bottom-0 transition duration-300 bg-gradient-to-t from-[rgba(0,0,0,0.5)] via-transparent via-100% to-transparent" />

            <FocusableProgressBar
              id={video?.id}
              focusKey="progressBar"
              duration={player.duration}
              currentTime={player.currentTime}
              language={video?.currentLanguage}
              previewThumbnailCount={video?.previewThumbnailCount}
              seekTime={localCurrentTime}
              isPlaying={player.isPlaying}
              player={player}
              handleDrag={handleDragStart}
              handleTouch={handleTouchStart}
            />

            {video && (
              <div className="absolute bottom-[171px] right-[104px] w-[286px] h-[78px] flex justify-end bg-transparent">
                {video.prevContent && (
                  <FocusableButton focusKey="prevButton" className={PLAYER_BUTTON_CSS} onClick={() => onPrev()}>
                    <img src={buttonPrev} alt="Previous" />
                  </FocusableButton>
                )}
                {video.nextContent && (
                  <FocusableButton focusKey="nextButton" className={PLAYER_BUTTON_CSS} onClick={() => onNext()}>
                    <img src={buttonNext} alt="Next" />
                  </FocusableButton>
                )}
                <FocusableButton
                  focusKey="languageButton"
                  className={PLAYER_BUTTON_CSS}
                  onClick={() => setControllerState('showLanguageModal')}
                >
                  <img src={buttonLanguage} alt="Language" />
                </FocusableButton>
              </div>
            )}

            <div className="absolute bottom-[56px] left-[216px] text-left h-[39px] text-white text-[33px] leading-[38.67px]">
              {formatSecondsToHMS(localCurrentTime)}
            </div>

            <div className="absolute bottom-[56px] right-[88px] text-right h-[39px] text-white text-[33px] leading-[38.67px]">
              {formatSecondsToHMS(player.duration)}
            </div>

            {video && (
              <div
                className="absolute text-white w-[1390px] h-[40px] left-[88px] bottom-[190px] leading-[40px] text-[40px] font-bold"
                style={{ filter: 'drop-shadow(0px 4px 12px rgba(0, 0, 0, 0.25))' }}
              >
                {video.title}
              </div>
            )}
          </div>
        )}

        {controllerState === 'showLanguageModal' && video && (
          <div
            className="absolute w-full h-full top-0 left-0"
            onClick={() => {
              setControllerState('hide');
            }}
            onContextMenu={() => {
              setControllerState('hide');
            }}
          >
            <div className="fixed w-[616px] top-[96px] right-[37px] py-[80px] px-[50px] bg-[rgba(0,0,0,0.9)] flex flex-col justify-center items-center text-white rounded-[20px]">
              <div className="flex flex-col p-[8px] w-full">{languageSelectComponents}</div>
            </div>
          </div>
        )}
      </div>
    </FocusContext.Provider>
  );
};
